import React from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { Close, AddCircleOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputField, FormRadioGroup, FormSwitch, InputField, InterestInputField } from 'components/form';
import { markupTypesOptions } from 'modules/desking/constants';
import { z } from 'zod';
import { FinanceStructureSchema } from '../model';
import { MarkupType } from '../../../types';

export const FeeArrayFields = () => {
  const classes = useStyles();

  const { control } = useFormContext<z.infer<typeof FinanceStructureSchema>>();

  const { remove, append, fields, update } = useFieldArray({ control, name: 'financeQuote.fee' });

  const watchFields = useWatch({
    control,
    name: 'financeQuote.fee',
  });

  return (
    <Box className={classes.arrayContainer}>
      {!!fields?.length &&
        fields.map((feeField, index) => (
          <Box key={feeField.id}>
            <Box className={classes.array}>
              <Typography component="span" variant="subtitle2">
                Fee: {index + 1}
              </Typography>

              <Box className={clsx('row', classes.arrayFields)}>
                <InputField control={control} label="Description" name={`financeQuote.fee.${index}.identifier`} />
                <Box>
                  {feeField.type === MarkupType.Flat ? (
                    <CurrencyInputField control={control} label="Fee ($)" name={`financeQuote.fee.${index}.fee`} />
                  ) : (
                    <InterestInputField label="Fee (%)" control={control} name={`financeQuote.fee.${index}.fee`} />
                  )}
                  <Controller
                    control={control}
                    name={`financeQuote.fee.${index}.type`}
                    render={({ field }) => (
                      <FormRadioGroup
                        row
                        options={markupTypesOptions}
                        {...field}
                        onChange={(event, value) =>
                          update(index, {
                            ...watchFields[index],
                            type: value as MarkupType,
                          })
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              <IconButton size="small" onClick={() => remove(index)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Controller
              control={control}
              name={`financeQuote.fee.${index}.financed`}
              render={({ field }) => (
                <FormSwitch label="Financed?" defaultChecked={field.value ? field.value : false} {...field} />
              )}
            />
          </Box>
        ))}

      <Box>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          startIcon={<AddCircleOutline />}
          onClick={() => append({ financed: true, fee: 0, identifier: '', type: MarkupType.Flat })}
        >
          Add Fee
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  arrayContainer: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
  array: {
    gap: '12px',
    display: 'flex',
    alignItems: 'baseline',
  },
  arrayFields: {
    flex: 1,
  },
});
